import { storeToRefs } from 'pinia';
import extendProducts from '~~/helpers/product/extendProducts';
import type { ProductVariantGql } from '~~/types/product/ProductVariantGql';
import { getProductSearchParams } from '~~/helpers/product/getProductSearchParams';
import { COMPLEMENTARY_PRODUCTS_LIMIT } from '~~/constants/commerceTools.js';
import { EXPONDO_MIDDLEWARE_ROUTES } from '~~/constants/routes';
import type { ComplementaryProducts } from '~~/types/product/ComplementaryProduct';
import { excludeProductsWithoutPrice } from '~~/helpers/commercetools/graphql/excludeProductsWithoutPrice';
import { complementaryProductsStore } from '~/stores/complementaryProducts';
import { DISABLE_STOCK_FILTER } from '~~/constants/queryOptions';

export default () => {

  const store = complementaryProductsStore();
  const {
    complementaryBaseProduct,
    complementaryProducts,
    cacheId,
    selectedProducts,
  } = storeToRefs(store);

  const { languageAndCountry, countryCode } = useLocalization();
  const { makeCommercetoolsRequest } = useExpondoMiddleware();
  const { languagePrefix } = usePrefix();
  const { query } = useRoute();

  const search = async (sku: string) => {

    store.clearSelectedProducts();

    if (cacheId.value === sku) {

      return;

    }
    store.setCacheId(sku);
    const productSearchParams = getProductSearchParams(
      {
        limit: COMPLEMENTARY_PRODUCTS_LIMIT,
        customFilters: excludeProductsWithoutPrice(countryCode.value),
      },
      {
        languageAndCountry: languageAndCountry.value,
        languagePrefix,
        ...(query[DISABLE_STOCK_FILTER] !== 'true' && { isOnStock: true }),
      },
    );
    const complementaryProductsData = await makeCommercetoolsRequest<ComplementaryProducts>({
      endpoint: EXPONDO_MIDDLEWARE_ROUTES.CT.GET_COMPLEMENTARY_PRODUCTS_BY_SKU,
      data: [
        { skus: [sku] }, productSearchParams,
      ],
    });
    const productsResults = complementaryProductsData?.data?.products?.data?.products?.results;
    const baseProductResults = complementaryProductsData?.data?.baseProduct?.data?.products?.results;
    const getExtendedProducts = (productsResults: ProductVariantGql[]) => {

      return extendProducts(productsResults, languageAndCountry.value, countryCode.value, { isPublished: true });
    
    };

    const products = getExtendedProducts(productsResults || []);
    const baseProduct = getExtendedProducts(baseProductResults || [])?.[0];
    store.setComplementaryProducts(products);
    store.setComplementaryBaseProduct(baseProduct);
  
  };

  return {
    ...store,
    search,
    complementaryBaseProduct,
    complementaryProducts,
    selectedProducts,
    toggleSelectedProduct: store.toggleSelectedProduct,
    clearSelectedProducts: store.clearSelectedProducts,
  };

};